import clock from '@plugins/dayjs';

/**
 * Return the date now
 * @param {String|Object}
 */
export const now = asString => {
    if (asString) {
        return clock().format('Do MMMM YYYY');
    }
    return clock();
};

/**
 * Return the previous financial year
 * @param {String|Object}
 */
export const getTaxYear = (date = null, options = {}) => {
    if (date === null || date === undefined || date === '') {
        return null;
    }

    if (date === 'last') {
        date = clock().subtract(1, 'year');
    }

    if (typeof date === 'string' && date.split('-').length === 2) {
        if (date.split('-')[0].length === 4 && date.split('-')[1].length === 2) {
            return date;
        }
    }

    const asString = options.asString || false;
    const abbreviate = options.abbreviate || false;

    const now = getTaxYearStart(date);

    if (!now) {
        return null;
    }

    let taxYearEndDate = now.clone().month(3).date(5);

    if (taxYearEndDate.isBefore(now)) {
        taxYearEndDate = taxYearEndDate.add(1, 'year');
    }

    let taxYearStartDate = taxYearEndDate.clone().subtract(1, 'year').date(6);

    const financialYear = [taxYearStartDate, taxYearEndDate];

    if (abbreviate) {
        return `${taxYearStartDate.format('YYYY')}-${taxYearEndDate.format('YY')}`;
    } else if (asString) {
        return financialYear.map(date => date.format('Do MMMM YYYY')).join(' - ');
    }

    return financialYear;
};

export const getTaxYearStart = (date = null) => {
    if (date === null || date === undefined || date === '') {
        return null;
    } else if (date === 'now') {
        date = clock().clone().format('YYYY-MM-DD');
    }

    let taxYearStart = null;

    if (typeof date === 'string' && date.length === 4 && !isNaN(Number(date))) {
        date = `${Number(date) + 1}-01-01`;
    } else if (typeof date === 'number' && String(date).length === 4) {
        date = `${date + 1}-01-01`;
    } else if (typeof date === 'string' && date.length === 2 && !isNaN(Number(date))) {
        date = `20${Number(date) + 1}-01-01`;
    } else if (typeof date === 'number' && String(date).length === 2) {
        date = `20${date + 1}-01-01`;
    }

    if (date) {
        taxYearStart = clock(date).clone().startOf('day');
    }

    return taxYearStart;
};

/**
 * Return the tax years from a start year
 * @param {String|Object}
 */
export const getTaxYears = (fromYear, valueKey = 'id', textKey = 'name', now = null) => {
    fromYear = fromYear || 1994;
    valueKey = valueKey || 'id';
    textKey = textKey || 'name';
    now = now || clock().clone().startOf('day');

    let currentFinancialYearStart = now.clone().month(3).date(6);

    if (currentFinancialYearStart.isAfter(now)) {
        currentFinancialYearStart = currentFinancialYearStart.subtract(1, 'year');
    }

    const currentYear = Number(currentFinancialYearStart.format('YYYY'));
    const count = currentYear - fromYear + 1;

    const years = Array.from({ length: count }, (_, i) => {
        const date = currentFinancialYearStart.clone().subtract(i, 'year');
        const taxYear = getTaxYear(date.clone());

        let startYear = taxYear[0].format('YYYY');

        let label = startYear;

        if (taxYear[1].format('YY') === '00') {
            label += '-' + taxYear[1].format('YYYY');
        } else {
            label += '-' + taxYear[1].format('YY');
        }

        if (date.isSame(currentFinancialYearStart)) {
            label += ' (Current)';
        }

        return { [valueKey]: startYear, [textKey]: label };
    });

    return years;
};

/**
 * Return the number of days until the end of the tax year
 * @param {String|Object}
 */
export const getDaysUntilTaxYearEnd = (date = null) => {
    const now = date ? clock(date).startOf('day') : clock().startOf('day');
    let fyEndDate = now.clone().month(3).date(5);

    if (now.isAfter(fyEndDate)) {
        fyEndDate = fyEndDate.add(1, 'year');
    }

    return fyEndDate.diff(now, 'days');
};

/**
 * Calculates a human-readable string for years and months.
 * @param {dayjs.Dayjs} start
 * @param {dayjs.Dayjs} end
 * @returns {string}
 */
export function yearsAndMonths(start, end) {
    let months = end.diff(start, 'month');
    let years = end.diff(start, 'year');
    months = months % 12;
    let duration = [];
    // Dayjs has no easy way of doing y+ms in a friendly way
    // so I decided to just do it manually for now
    if (years === 1) {
        duration.push('1 year');
    } else if (years > 1) {
        duration.push(`${years} years`);
    }

    if (months === 1) {
        duration.push('1 month');
    } else if (months > 1) {
        duration.push(`${months} months`);
    }

    return duration.join(', ');
}

export const normaliseDurationDisplayMode = mode => {
    if (['auto', 'months', 'years'].includes(mode)) {
        return mode;
    }

    return 'auto';
};

export const formatDurationMixedFromMonths = (months, pluralise) => {
    const parsedMonths = Number.isFinite(Number(months)) ? Number(months) : 0;
    const duration = clock.duration({ months: parsedMonths });
    const years = Math.floor(duration.asYears());
    const remainingMonths = parsedMonths - years * 12;
    const parts = [];

    if (years > 0) {
        parts.push(`${years} ${pluralise('year', years)}`);
    }

    if (remainingMonths > 0 || years === 0) {
        parts.push(`${remainingMonths} ${pluralise('month', remainingMonths)}`);
    }

    return parts.join(' ');
};

export const formatDurationDecimalYearsFromMonths = (months, pluralise) => {
    const parsedMonths = Number.isFinite(Number(months)) ? Number(months) : 0;
    const yearsValue = Number(clock.duration({ months: parsedMonths }).asYears().toFixed(2));
    return `${yearsValue} ${pluralise('year', yearsValue)}`;
};

export const formatDurationLabelFromMonths = (months, mode, pluralise) => {
    const parsedMonths = Number.isFinite(Number(months)) ? Number(months) : 0;
    const displayMode = normaliseDurationDisplayMode(mode);

    if (displayMode === 'auto') {
        return formatDurationMixedFromMonths(parsedMonths, pluralise);
    }

    if (displayMode === 'years') {
        return formatDurationDecimalYearsFromMonths(parsedMonths, pluralise);
    }

    return `${parsedMonths} ${pluralise('month', parsedMonths)}`;
};

/**
 * Time until a date
 * @param {String} value
 * @returns {String}
 */
export const timeUntil = value => {
    value = clock(value);

    if (!value.isValid()) {
        return 'in the future';
    }

    return value.fromNow();
};

/**
 * Datetime Formatting to ISO 8601
 * @param {String} value
 * @returns {String}
 */
export const toIso8601 = (value, local = false) => {
    if (!value) {
        return null;
    }

    let date = value;

    if (date instanceof Date || date instanceof clock) {
        date = date.toISOString();
    } else {
        const parsedAsUnix = clock.unix(Number(date));
        if (parsedAsUnix.isValid() && parsedAsUnix.unix().toString() === String(date)) {
            date = parsedAsUnix.toISOString();
        }
    }

    // Convert 'DD/MM/YYYY' to 'YYYY-MM-DD'
    if (/\d{2}\/\d{2}\/\d{4}/.test(date)) {
        date = date.split('/').reverse().join('-');
    }

    // Append time as 00:00:00 if not set
    if (!date.includes(' ') && !date.includes('T')) {
        date += 'T00:00:00';
    }

    // Append 'Z' if date doesn't specify a timezone and it's not local time
    if (!local && !date.endsWith('Z') && !date.includes('+')) {
        date += 'Z';
    }

    try {
        date = local ? clock(date) : clock.utc(date);

        // Convert to user's local time in ISO 8601 format
        date = date.tz(clock.tz.guess()).toISOString();

        // remove milliseconds but keep timezone offset
        return date.replace(/\.\d{3}Z$/, 'Z');
    } catch (error) {
        return null;
    }
};

/**
 * Date Formatting
 * @param {String} value
 * @param {String} format
 * @returns {String}
 */
export const getDateIntervals = (intervalType, startDate, endDate, format = 'YYYY-MM-DD') => {
    const validIntervals = ['days', 'weeks', 'months', 'years'];

    if (!validIntervals.includes(intervalType)) {
        throw new Error(`Invalid interval type. Use one of: ${validIntervals.join(', ')}`);
    }

    const intervals = [];

    let currentDate = clock(startDate);
    const finalDate = clock(endDate);

    while (currentDate.isBefore(finalDate) || currentDate.isSame(finalDate, intervalType)) {
        intervals.push(currentDate.format(format));
        currentDate = currentDate.add(1, intervalType);
    }

    return intervals;
};

/**
 * Datetime Formatting
 * @param {String} value
 * @param {String} format
 * @returns {String}
 */
export const toDatetime = (value, format = 'DD/MM/YYYY HH:mm:ss', local = true) => {
    if (!value) {
        return null;
    }

    value = toIso8601(value);

    if (!value) {
        return null;
    }

    let date = clock.utc(value);

    if (!date.isValid()) {
        console.error(new Error('Datetime is not a valid date object:' + value));
        return null;
    }

    if (format === false) {
        return date;
    }

    return local ? date.local().format(format) : date.format(format);
};

/**
 * Date Formatting
 * @param {String} value
 * @param {String} format
 * @returns {String}
 */
export const toDate = (value, format = 'DD/MM/YYYY', local = true) => {
    if (!value) {
        return null;
    }

    value = toIso8601(value);

    if (!value) {
        return null;
    }

    let date = clock.utc(value);

    if (!date.isValid()) {
        console.error(new Error('Date is not a valid date object:' + value));
        return null;
    }

    if (format === false) {
        return date;
    }

    return local ? date.local().format(format) : date.format(format);
};
